import supabase from 'modules/core/supabase/supabase';
import { mapLogoBacking } from 'modules/organizer/domain/data-mappers/logo-backing.data-mapper';
import { LogoBacking } from 'modules/organizer/domain/interfaces/logo-backing';
import { baseApi } from 'shared/api/base-api';
import { toApiError } from 'shared/errors';

export interface UpdateLogoBackingArgs {
  /** Id организации (backend `tournamentId`). */
  organizerId: number;
  /** Ключ `backgroundColors` (`shared/constants/backgrounds`). */
  type: number;
}

/**
 * Подложка логотипа организации — строка Supabase `organizer_logo_backing`, в кэш она ложится уже доменным
 * `LogoBacking` (`mapLogoBacking`). Данные живут не в бэкенде, поэтому
 * `queryFn` поверх `supabase.organizerLogoBacking`, как `template-editor/store/board-templates-api.ts`.
 *
 * Таблица гасит ошибку сама и отдаёт `null` — для чтения это «подложка не задана». Сохранение кладёт ответ в кэш
 * чтения (pessimistic update, [API-CACHE]): форма профиля получает новый цвет без второго запроса. Пустой ответ
 * сохранения кэш не трогает — иначе сбой записи сбросил бы цвет на экране к первому.
 */
export const logoBackingApi = baseApi.injectEndpoints({
  endpoints: (build) => ({
    getLogoBacking: build.query<LogoBacking | null, number>({
      async queryFn(organizerId) {
        try {
          return { data: mapLogoBacking(await supabase.organizerLogoBacking.get({ tournamentId: organizerId })) };
        } catch (error) {
          return { error: toApiError(error) };
        }
      },
    }),

    updateLogoBacking: build.mutation<LogoBacking | null, UpdateLogoBackingArgs>({
      async queryFn({ organizerId, type }) {
        try {
          return { data: mapLogoBacking(await supabase.organizerLogoBacking.update({ tournamentId: organizerId, type })) };
        } catch (error) {
          return { error: toApiError(error) };
        }
      },
      async onQueryStarted({ organizerId }, { dispatch, queryFulfilled }) {
        let data: LogoBacking | null;
        try {
          ({ data } = await queryFulfilled);
        } catch {
          return;
        }
        if (!data) return;

        const patch = dispatch(logoBackingApi.util.updateQueryData('getLogoBacking', organizerId, () => data));
        if (patch.patches.length === 0) {
          dispatch(logoBackingApi.util.upsertQueryData('getLogoBacking', organizerId, data));
        }
      },
    }),
  }),
  overrideExisting: false,
});

export const { useGetLogoBackingQuery, useUpdateLogoBackingMutation } = logoBackingApi;

export default logoBackingApi;
